import type { CardLayout, ImgBlobs } from '@components/Card2/CardHeader/types';
import { WEB_EDITOR_VERSION, imageLocationsFromLayout } from '@components/Card2/CardHeader/utilities';
import type { CardFlavour } from '@components/Card2/types';
import { CardHeaderEditContext } from '@context/card-header-edit-2';
import { useContext, useMemo } from 'react';

import type { CardImages } from '.cache/__types__';

type UseCardImageUrlsArgs = {
  editMode: boolean;
  cardImages?: CardImages | null;
  imgBlobs?: ImgBlobs;
  layout?: CardLayout;
  flavour?: CardFlavour;
};

export const useCardImageUrls = ({ cardImages, imgBlobs, layout, flavour }: UseCardImageUrlsArgs) => {
  const { editState } = useContext(CardHeaderEditContext);

  const images = useMemo(() => {
    const previews = {
      left: editState?.type === 'profile',
      right: editState?.type === 'logo',
    };

    const originalImages = {
      profile: {
        ...cardImages?.profile_picture,
        metadata: {
          ...cardImages?.profile_picture?.metadata,
          editor_version: WEB_EDITOR_VERSION,
        },
        value_provider:
          editState.type === 'profile'
            ? editState.image?.value_provider
            : (cardImages?.profile_picture?.value_provider ?? undefined),
        edited_url:
          editState.type === 'profile'
            ? editState.image?.edited_url
            : cardImages?.profile_picture?.edited_url || imgBlobs?.profile.imageUrl,
      },
      logo: {
        ...cardImages?.company_logo,
        value_provider:
          editState.type === 'logo'
            ? editState.image?.value_provider
            : (cardImages?.company_logo?.value_provider ?? undefined),
        edited_url:
          editState.type === 'logo'
            ? editState.image?.edited_url
            : cardImages?.company_logo?.edited_url || imgBlobs?.logo.imageUrl,
      },
      cover: {
        ...cardImages?.cover_photo,
        value_provider:
          editState.type === 'cover'
            ? editState.image?.value_provider
            : (cardImages?.cover_photo?.value_provider ?? undefined),
        edited_url:
          editState.type === 'cover'
            ? editState.image?.edited_url
            : cardImages?.cover_photo?.edited_url || imgBlobs?.cover.imageUrl,
      },
    };

    const locations = layout ? imageLocationsFromLayout(layout, previews) : {};

    return {
      ...originalImages,
      banner: locations.banner ? originalImages[locations.banner] : undefined,
      left: locations.left ? originalImages[locations.left] : undefined,
      right: locations.right ? originalImages[locations.right] : undefined,
    };
  }, [
    cardImages?.company_logo,
    cardImages?.cover_photo,
    cardImages?.profile_picture,
    editState,
    imgBlobs?.cover.imageUrl,
    imgBlobs?.logo.imageUrl,
    imgBlobs?.profile.imageUrl,
    layout,
  ]);

  return images;
};
